// Users & Roles taxonomy + permission model (§2, §5.1).
// Seat types: Paid · Viewer (Viewer = read-only, no permission choices).
// Roles: Admin · Salesperson · Field Tech (a role, not a seat type; Field
// Tech's seat type is Viewer).

export const SEAT_TYPES = [
  { value: 'paid', label: 'Paid', description: 'Occupies a licensed seat. Can be assigned a role + permissions.' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access. No permission choices.' },
];

export const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access — no permission checklist.' },
  { value: 'salesperson', label: 'Salesperson', description: 'Quotes, jobs, customers, leads.' },
  { value: 'field_tech', label: 'Field Tech', description: 'Field work orders. Seat type is Viewer.' },
];

// Permission groups + individual permissions (§2 closed inventory).
export const PERMISSION_GROUPS = [
  { group: 'Customers', permissions: [
    { key: 'customers.add_edit', label: 'Add / edit customers' },
  ] },
  { group: 'Jobs', permissions: [
    { key: 'jobs.add_job_order', label: 'Add job order / convert quote' },
    { key: 'jobs.edit_job_order', label: 'Edit job order' },
    { key: 'jobs.add_co', label: 'Add change order' },
    { key: 'jobs.edit_co', label: 'Edit change order' },
    { key: 'jobs.add_wo', label: 'Add work order' },
    { key: 'jobs.edit_wo', label: 'Edit work order' },
  ] },
  { group: 'Leads', permissions: [
    { key: 'leads.add_edit', label: 'Add / edit leads' },
  ] },
  { group: 'Pricing', permissions: [
    { key: 'pricing.show_line_item', label: 'Show line-item pricing' },
    { key: 'pricing.show_cost_breakdown', label: 'Show cost breakdown' },
    { key: 'pricing.view_edit_margin', label: 'View / edit margin % and $' },
  ] },
  { group: 'Quotes', permissions: [
    { key: 'quotes.add_edit', label: 'Add / edit quotes' },
  ] },
  { group: 'Reports', permissions: [
    { key: 'reports.view', label: 'View reports' },
  ] },
];

export const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key));

// IDI-762 D2: Manage's seed — every toggle on, then the saved map over it.
// Nothing saved (or an unreadable map) reads as on, as the app and API do.
export function manageSeedPermissions(saved) {
  const seed = Object.fromEntries(ALL_PERMISSION_KEYS.map((k) => [k, true]));
  return saved && typeof saved === 'object' && !Array.isArray(saved) ? { ...seed, ...saved } : seed;
}

// The seven a Paid Salesperson needs to read "Editor" in the Users list
// (computeSeatKind). IDI-805: no longer Add User's pre-fill, which is all on.
const SALESPERSON_DEFAULTS = [
  'customers.add_edit', 'leads.add_edit', 'quotes.add_edit',
  'jobs.add_job_order', 'jobs.edit_job_order', 'jobs.add_wo',
  'pricing.show_line_item',
];

export function defaultPermissionsForRole(role) {
  const map = {};
  if (role === 'admin') {
    ALL_PERMISSION_KEYS.forEach((k) => { map[k] = true; });
  } else if (role === 'salesperson') {
    // IDI-805 D1: every toggle starts on; the admin removes what they don't want.
    ALL_PERMISSION_KEYS.forEach((k) => { map[k] = true; });
  } else {
    ALL_PERMISSION_KEYS.forEach((k) => { map[k] = false; });
  }
  return map;
}

// A non-admin Paid user sees the permission checklist (§5.1).
export function needsPermissionChecklist(seatType, role) {
  return seatType === 'paid' && role !== 'admin';
}

// Iter18 §2.3 — Seat-kind display taxonomy.
// The UI distinguishes four "kinds" in the Users list:
//   admin  — role=admin (full access, no checklist)
//   editor — role=salesperson + paid seat + full salesperson permissions granted
//   sales  — role=salesperson + paid seat + partial permissions
//   viewer — anything else (unpaid, field_tech, missing seat_type, etc.)
//
// Returns { value, label, description }.
export const SEAT_KINDS = {
  admin:  { value: 'admin',  label: 'Admin',  description: 'Full access. No permission checklist.' },
  editor: { value: 'editor', label: 'Editor', description: 'Paid seat with full salesperson permissions.' },
  sales:  { value: 'sales',  label: 'Sales',  description: 'Paid seat with partial permissions.' },
  viewer: { value: 'viewer', label: 'Viewer', description: 'Read-only. No permission choices.' },
};

export function computeSeatKind(user) {
  if (!user) return SEAT_KINDS.viewer;
  if (user.role === 'admin') return SEAT_KINDS.admin;
  if (user.seat_type === 'paid' && user.role === 'salesperson') {
    const perms = user.permissions || {};
    const grantedFromDefaults = SALESPERSON_DEFAULTS.filter((k) => perms[k]).length;
    // "Editor" = every default salesperson permission granted; anything less = "Sales".
    if (grantedFromDefaults === SALESPERSON_DEFAULTS.length) return SEAT_KINDS.editor;
    return SEAT_KINDS.sales;
  }
  return SEAT_KINDS.viewer;
}
